import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classes from './LanguageSwitcher.module.css';
import {
  LOCALES,
  LOCALE_NAMES,
  LOCALE_FLAGS,
  DEFAULT_LOCALE,
  localePath,
  storeLocale,
} from '../i18n/config';
import { useI18n } from '../i18n/LanguageContext';
import { slugFor, idFromSlug } from '../content/slugs';

/**
 * Maps the URL you are on to the same page in another locale.
 *
 * Article slugs are translated, so `/tr/blog/manifestasyon-nedir` has to
 * resolve back to a post id before it can become `/de/blog/was-ist-manifestation`.
 * Anything we cannot map that way (an unknown slug, a 404) falls back to the
 * same route path under the target locale.
 */
function useSwitchHref() {
  const { locale } = useI18n();
  const { pathname } = useLocation();

  return useCallback(
    (target) => {
      let route = pathname;
      if (locale !== DEFAULT_LOCALE) {
        const prefix = `/${locale}`;
        if (route === prefix) route = '/';
        else if (route.startsWith(`${prefix}/`)) route = route.slice(prefix.length);
      }

      const post = route.match(/^\/blog\/([^/]+)$/);
      if (post) {
        const id = idFromSlug(post[1], locale);
        if (id) return localePath(target, `/blog/${slugFor(id, target)}`);
      }

      return localePath(target, route || '/');
    },
    [locale, pathname]
  );
}

function LanguageSwitcher() {
  const { locale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const hrefFor = useSwitchHref();

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={classes.wrap} ref={wrapRef}>
      <button
        type="button"
        className={classes.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('nav.languageAria')}
      >
        <svg className={classes.globe} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className={classes.current}>{LOCALE_NAMES[locale]}</span>
        <span className={classes.code}>{locale.toUpperCase()}</span>
        <svg className={`${classes.chevron} ${open ? classes.chevronOpen : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <ul className={classes.menu}>
          {LOCALES.map((loc) => (
            <li key={loc}>
              {/* Real links, so each locale is also a crawlable path for search engines. */}
              <Link
                to={hrefFor(loc)}
                hrefLang={loc}
                className={`${classes.option} ${loc === locale ? classes.active : ''}`}
                aria-current={loc === locale ? 'true' : undefined}
                onClick={() => {
                  storeLocale(loc);
                  setOpen(false);
                }}
              >
                <span className={classes.flag} aria-hidden="true">
                  {LOCALE_FLAGS[loc]}
                </span>
                {LOCALE_NAMES[loc]}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LanguageSwitcher;
